import { Building2, Check } from 'lucide-react'
import { Button } from '../../core/index.js'
import type { SchedulingRules } from '../types.js'

/**
 * The order the census works in, above the staffing plan: the units, then the
 * plan, then the counts. Each step is done or not from the draft, so a plan
 * set before the units says plainly that it requires nothing yet. Shown only
 * while one of the first two is missing.
 */

interface CensusSetupStep {
  label: string
  detail: string
  done: boolean
}

export function censusSetupSteps(rules: Pick<SchedulingRules, 'units' | 'staffing'>): CensusSetupStep[] {
  const hasUnits = rules.units.length > 0
  return [
    { label: 'List the units', detail: 'On the Details tab, each with its beds and planned census. The census and what it requires are reckoned per unit.', done: hasUnits },
    { label: 'Set the staffing plan', detail: hasUnits ? 'Below: how a day’s census becomes seats.' : 'Below. It can be set now, but requires nothing until the units are listed.', done: rules.staffing !== null },
    { label: 'Record the census', detail: 'Each day on the Dashboard’s Today tab, or from the EHR as a partner.', done: false },
  ]
}

interface CensusSetupStepsProps {
  rules: Pick<SchedulingRules, 'units' | 'staffing'>
  /** Opens the Details tab, where the units are listed. */
  onOpenUnits?: () => void
}

export function CensusSetupSteps({ rules, onOpenUnits }: CensusSetupStepsProps) {
  const steps = censusSetupSteps(rules)
  if (steps[0]?.done && steps[1]?.done) {
    return null
  }
  return (
    <div className="rounded border border-coral/40 bg-coral/5 p-3">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-coral">Before the census can say what a month requires</div>
      <ol className="flex flex-col gap-1.5">
        {steps.map((step, index) => (
          <li key={step.label} className="flex items-start gap-2 text-[11px] leading-relaxed">
            <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${step.done ? 'bg-green text-white' : 'border border-marine/40 text-marine'}`}>{step.done ? <Check className="h-3 w-3" /> : index + 1}</span>
            <span>
              <span className={`font-bold ${step.done ? 'text-ink-mid line-through' : 'text-ink'}`}>{step.label}</span> <span className="text-ink-mid">{step.detail}</span>
            </span>
          </li>
        ))}
      </ol>
      {!steps[0]?.done && onOpenUnits && (
        <Button icon={Building2} size="sm" className="mt-2" onClick={onOpenUnits}>
          Set up the units
        </Button>
      )}
    </div>
  )
}
